/**
 * HubSpotSync - Main sync UI for importing from and exporting to HubSpot.
 *
 * Provides a tabbed interface with two entity tabs (Contacts, Companies)
 * and two mode toggles (Import, Export) within each tab.
 *
 * Import Mode:
 * - Search bar to query HubSpot records
 * - Paginated table with checkboxes for record selection
 * - "Import Selected" button with import-as dropdown (contact vs user)
 * - "Already imported" badges on records that are already linked locally
 * - Results summary after import (imported, updated, skipped, errors)
 *
 * Export Mode:
 * - Fetches and displays local contacts/users/companies
 * - Checkbox selection for records to push to HubSpot
 * - "Synced" badges on records that already have a hubspot_*_id
 * - Results summary after export
 *
 * Only visible when HubSpot is connected. Requires admin/developer role.
 */
import React, { useState, useEffect, useCallback } from 'react';
import * as hubspotService from '../../../services/hubspotService';
import type {
    HubSpotContact,
    HubSpotCompany,
    ImportResult,
    ExportResult,
} from '../../../services/hubspotService';
import { api } from '../../../services/api';
import { Checkbox } from '../../atoms/Checkbox';
import { SearchBar } from '../../molecules/SearchBar';
import { Pagination } from '../../molecules/Pagination';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Which entity type is being synced */
type EntityTab = 'contacts' | 'companies';

/** Whether we're importing from HubSpot or exporting to HubSpot */
type SyncMode = 'import' | 'export';

/** Local contact record shape (for export view) */
interface LocalContact {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    email: string | null;
    phone: string | null;
    title: string | null;
    company_id: number;
    hubspot_contact_id: string | null;
    status: string;
}

/** Local user record shape (for export view) */
interface LocalUser {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string | null;
    role_names: string[];
    company_id: number | null;
    hubspot_contact_id: string | null;
    status: string;
}

/** Local company record shape (for export view) */
interface LocalCompany {
    company_id: number;
    name: string;
    phone: string | null;
    billing_email: string | null;
    hubspot_company_id: string | null;
    status: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const HubSpotSync: React.FC<{ className?: string }> = ({ className }) => {
    // ----- Tab & mode state -----
    const [entityTab, setEntityTab] = useState<EntityTab>('contacts');
    const [syncMode, setSyncMode] = useState<SyncMode>('import');

    // ----- Import state -----
    const [searchQuery, setSearchQuery] = useState('');
    const [hubspotContacts, setHubspotContacts] = useState<HubSpotContact[]>([]);
    const [hubspotCompanies, setHubspotCompanies] = useState<HubSpotCompany[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [browsing, setBrowsing] = useState(false);
    const [selectedHubspotIds, setSelectedHubspotIds] = useState<Set<string>>(new Set());
    const [importAs, setImportAs] = useState<'contact' | 'user'>('contact');
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<ImportResult | null>(null);

    // ----- Export state -----
    const [localContacts, setLocalContacts] = useState<LocalContact[]>([]);
    const [localUsers, setLocalUsers] = useState<LocalUser[]>([]);
    const [localCompanies, setLocalCompanies] = useState<LocalCompany[]>([]);
    const [loadingLocal, setLoadingLocal] = useState(false);
    const [selectedLocalContactIds, setSelectedLocalContactIds] = useState<Set<number>>(new Set());
    const [selectedLocalUserIds, setSelectedLocalUserIds] = useState<Set<number>>(new Set());
    const [selectedLocalCompanyIds, setSelectedLocalCompanyIds] = useState<Set<number>>(new Set());
    const [exporting, setExporting] = useState(false);
    const [exportResult, setExportResult] = useState<ExportResult | null>(null);

    // ----- Export: per-table search and pagination state.
    // Each table (contacts, users, companies) is paginated independently so a
    // user can drill into one without losing position in another. Selection
    // state above (Set<number>) persists across pages — picking on page 1 then
    // navigating to page 2 keeps the page-1 selection intact. -----
    const [exportContactsSearch, setExportContactsSearch] = useState('');
    const [exportContactsPage, setExportContactsPage] = useState(1);
    const [exportContactsLastPage, setExportContactsLastPage] = useState(1);
    const [exportContactsTotal, setExportContactsTotal] = useState(0);
    const [exportUsersSearch, setExportUsersSearch] = useState('');
    const [exportUsersPage, setExportUsersPage] = useState(1);
    const [exportUsersLastPage, setExportUsersLastPage] = useState(1);
    const [exportUsersTotal, setExportUsersTotal] = useState(0);
    const [exportCompaniesSearch, setExportCompaniesSearch] = useState('');
    const [exportCompaniesPage, setExportCompaniesPage] = useState(1);
    const [exportCompaniesLastPage, setExportCompaniesLastPage] = useState(1);
    const [exportCompaniesTotal, setExportCompaniesTotal] = useState(0);

    // ----- Import: company selector for contact imports -----
    const [companies, setCompanies] = useState<{ company_id: number; name: string }[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    // ----- Error state -----
    const [error, setError] = useState<string | null>(null);

    // Fetch company list for the import company selector
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const { data } = await api.get<{ data: { company_id: number; name: string }[] }>('/companies');
                const list = data?.data || data;
                setCompanies(Array.isArray(list) ? list : []);
                // Auto-select the first company if available
                if (Array.isArray(list) && list.length > 0 && !selectedCompanyId) {
                    setSelectedCompanyId(String(list[0].company_id));
                }
            } catch {
                // Silently fail — the user can still select a company manually
            }
        };
        fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ----- Clear selections when switching tabs or modes -----
    useEffect(() => {
        setSelectedHubspotIds(new Set());
        setSelectedLocalContactIds(new Set());
        setSelectedLocalUserIds(new Set());
        setSelectedLocalCompanyIds(new Set());
        setImportResult(null);
        setExportResult(null);
        setError(null);
        setHubspotContacts([]);
        setHubspotCompanies([]);
        setNextCursor(null);
    }, [entityTab, syncMode]);

    // ----- Fetch local records when switching to export mode, when the user
    // changes the search box, or when they click Next/Previous. Pagination
    // state is per-table (contacts/users/companies) so they don't interfere. -----
    useEffect(() => {
        if (syncMode !== 'export') return;

        /* Read Laravel pagination meta if present, else treat as a flat array */
        const parsePaginated = <T,>(payload: any): { items: T[]; lastPage: number; total: number } => {
            if (Array.isArray(payload)) {
                return { items: payload, lastPage: 1, total: payload.length };
            }
            const items: T[] = Array.isArray(payload?.data) ? payload.data : [];
            return {
                items,
                lastPage: payload?.last_page ?? 1,
                total: payload?.total ?? items.length,
            };
        };

        const fetchLocal = async () => {
            setLoadingLocal(true);
            try {
                if (entityTab === 'contacts') {
                    /* Fire contacts and users in parallel — each carries its own
                       search + page params so they paginate independently. */
                    const contactsParams: Record<string, string> = { page: String(exportContactsPage) };
                    if (exportContactsSearch) contactsParams.search = exportContactsSearch;
                    const usersParams: Record<string, string> = { page: String(exportUsersPage) };
                    if (exportUsersSearch) usersParams.search = exportUsersSearch;
                    const [contactsRes, usersRes] = await Promise.all([
                        api.get<any>('/contacts', contactsParams),
                        api.get<any>('/users', usersParams),
                    ]);
                    const c = parsePaginated<LocalContact>(contactsRes.data);
                    const u = parsePaginated<LocalUser>(usersRes.data);
                    setLocalContacts(c.items);
                    setExportContactsLastPage(c.lastPage);
                    setExportContactsTotal(c.total);
                    setLocalUsers(u.items);
                    setExportUsersLastPage(u.lastPage);
                    setExportUsersTotal(u.total);
                } else {
                    const companiesParams: Record<string, string> = { page: String(exportCompaniesPage) };
                    if (exportCompaniesSearch) companiesParams.search = exportCompaniesSearch;
                    const res = await api.get<any>('/companies', companiesParams);
                    const co = parsePaginated<LocalCompany>(res.data);
                    setLocalCompanies(co.items);
                    setExportCompaniesLastPage(co.lastPage);
                    setExportCompaniesTotal(co.total);
                }
            } catch {
                setError('Failed to load local records');
            } finally {
                setLoadingLocal(false);
            }
        };
        fetchLocal();
    }, [
        syncMode,
        entityTab,
        exportContactsSearch,
        exportContactsPage,
        exportUsersSearch,
        exportUsersPage,
        exportCompaniesSearch,
        exportCompaniesPage,
    ]);

    /* Reset to page 1 whenever a search filter changes — otherwise the user
       could be stranded on a page index that no longer exists in the new
       filtered result set. */
    useEffect(() => { setExportContactsPage(1); }, [exportContactsSearch]);
    useEffect(() => { setExportUsersPage(1); }, [exportUsersSearch]);
    useEffect(() => { setExportCompaniesPage(1); }, [exportCompaniesSearch]);

    // =================================================================
    // IMPORT HANDLERS
    // =================================================================

    /**
     * Browse or search HubSpot records. Replaces the current results
     * (does not append — use loadMore for pagination).
     */
    const handleBrowse = useCallback(async (cursor?: string) => {
        setBrowsing(true);
        setError(null);
        setImportResult(null);

        try {
            const params = {
                search: searchQuery || undefined,
                after: cursor || undefined,
                limit: 50,
            };

            if (entityTab === 'contacts') {
                const data = await hubspotService.getContacts(params);
                if (cursor) {
                    // Append for "Load More"
                    setHubspotContacts((prev) => [...prev, ...data.results]);
                } else {
                    setHubspotContacts(data.results);
                }
                setNextCursor(data.next_cursor);
            } else {
                const data = await hubspotService.getCompanies(params);
                if (cursor) {
                    setHubspotCompanies((prev) => [...prev, ...data.results]);
                } else {
                    setHubspotCompanies(data.results);
                }
                setNextCursor(data.next_cursor);
            }
        } catch (err: unknown) {
            const apiError = err as { data?: { message?: string }; message?: string };
            setError(apiError?.data?.message || apiError?.message || 'Failed to fetch HubSpot records');
        } finally {
            setBrowsing(false);
        }
    }, [entityTab, searchQuery]);

    /** Toggle a HubSpot record's selection state */
    const toggleHubspotSelection = (hubspotId: string) => {
        setSelectedHubspotIds((prev) => {
            const next = new Set(prev);
            if (next.has(hubspotId)) {
                next.delete(hubspotId);
            } else {
                next.add(hubspotId);
            }
            return next;
        });
    };

    /** Select/deselect all visible HubSpot records */
    const toggleAllHubspot = (records: { hubspot_id: string; already_imported: boolean }[]) => {
        const importable = records.filter((r) => !r.already_imported);
        const allSelected = importable.every((r) => selectedHubspotIds.has(r.hubspot_id));

        if (allSelected) {
            // Deselect all
            setSelectedHubspotIds((prev) => {
                const next = new Set(prev);
                importable.forEach((r) => next.delete(r.hubspot_id));
                return next;
            });
        } else {
            // Select all importable
            setSelectedHubspotIds((prev) => {
                const next = new Set(prev);
                importable.forEach((r) => next.add(r.hubspot_id));
                return next;
            });
        }
    };

    /** Import the selected HubSpot records */
    const handleImport = async () => {
        if (selectedHubspotIds.size === 0) return;

        // For contact imports, a company is required
        if (entityTab === 'contacts' && !selectedCompanyId) {
            setError('Please select a company to associate contacts with');
            return;
        }

        setImporting(true);
        setError(null);

        try {
            const ids = Array.from(selectedHubspotIds);
            let result: ImportResult;

            if (entityTab === 'contacts') {
                result = await hubspotService.importContacts(ids, importAs, selectedCompanyId);
            } else {
                result = await hubspotService.importCompanies(ids);
            }

            setImportResult(result);
            setSelectedHubspotIds(new Set());
            // Refresh the browse results to update "already_imported" badges
            handleBrowse();
        } catch (err: unknown) {
            const apiError = err as { data?: { message?: string }; message?: string };
            setError(apiError?.data?.message || apiError?.message || 'Import failed');
        } finally {
            setImporting(false);
        }
    };

    // =================================================================
    // EXPORT HANDLERS
    // =================================================================

    /** Export selected local records to HubSpot */
    const handleExport = async () => {
        setExporting(true);
        setError(null);
        setExportResult(null);

        try {
            let result: ExportResult;

            if (entityTab === 'contacts') {
                result = await hubspotService.exportContacts(
                    Array.from(selectedLocalContactIds),
                    Array.from(selectedLocalUserIds)
                );
            } else {
                result = await hubspotService.exportCompanies(Array.from(selectedLocalCompanyIds));
            }

            setExportResult(result);
            setSelectedLocalContactIds(new Set());
            setSelectedLocalUserIds(new Set());
            setSelectedLocalCompanyIds(new Set());
        } catch (err: unknown) {
            const apiError = err as { data?: { message?: string }; message?: string };
            setError(apiError?.data?.message || apiError?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    // =================================================================
    // RENDER
    // =================================================================

    return (
        <div className={`hubspot-sync bg-white rounded-lg shadow-sm border border-gray-200 p-24px mt-16px ${className ?? ''}`} data-ref="organism-hubspot-sync">
            {/* ---- Entity tab bar (Contacts / Companies) ---- */}
            <div className="hubspot-sync__tabs flex flex-col tablet:flex-row tablet:items-center gap-12px mb-16px">
                <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    <button
                        onClick={() => setEntityTab('contacts')}
                        className={`px-16px py-8px text-14px font-medium min-h-44px ${
                            entityTab === 'contacts'
                                ? 'bg-brand-primary text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Contacts
                    </button>
                    <button
                        onClick={() => setEntityTab('companies')}
                        className={`px-16px py-8px text-14px font-medium min-h-44px ${
                            entityTab === 'companies'
                                ? 'bg-brand-primary text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Companies
                    </button>
                </div>

                {/* ---- Mode toggle (Import / Export) ---- */}
                <div className="flex rounded-lg border border-gray-200 overflow-hidden tablet:ml-auto">
                    <button
                        onClick={() => setSyncMode('import')}
                        className={`px-16px py-8px text-14px font-medium min-h-44px ${
                            syncMode === 'import'
                                ? 'bg-brand-accent text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Import from HubSpot
                    </button>
                    <button
                        onClick={() => setSyncMode('export')}
                        className={`px-16px py-8px text-14px font-medium min-h-44px ${
                            syncMode === 'export'
                                ? 'bg-brand-accent text-white'
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Export to HubSpot
                    </button>
                </div>
            </div>

            {/* ---- Error banner ---- */}
            {error && (
                <div className="mb-16px p-12px rounded-lg bg-red-50 border border-red-200 text-14px text-red-700">
                    {error}
                </div>
            )}

            {/* ---- Import/Export result banner ---- */}
            {importResult && <ResultBanner type="import" result={importResult} />}
            {exportResult && <ResultBanner type="export" result={exportResult} />}

            {/* ---- Import Mode UI ---- */}
            {syncMode === 'import' && (
                <ImportView
                    entityTab={entityTab}
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    onBrowse={() => handleBrowse()}
                    onLoadMore={() => handleBrowse(nextCursor || undefined)}
                    browsing={browsing}
                    contacts={hubspotContacts}
                    companies={hubspotCompanies}
                    nextCursor={nextCursor}
                    selectedIds={selectedHubspotIds}
                    onToggle={toggleHubspotSelection}
                    onToggleAll={() =>
                        toggleAllHubspot(
                            entityTab === 'contacts' ? hubspotContacts : hubspotCompanies
                        )
                    }
                    importAs={importAs}
                    onImportAsChange={setImportAs}
                    companyOptions={companies}
                    selectedCompanyId={selectedCompanyId}
                    onCompanyChange={setSelectedCompanyId}
                    onImport={handleImport}
                    importing={importing}
                />
            )}

            {/* ---- Export Mode UI ---- */}
            {syncMode === 'export' && (
                <ExportView
                    entityTab={entityTab}
                    loading={loadingLocal}
                    contacts={localContacts}
                    users={localUsers}
                    companies={localCompanies}
                    selectedContactIds={selectedLocalContactIds}
                    selectedUserIds={selectedLocalUserIds}
                    selectedCompanyIds={selectedLocalCompanyIds}
                    onToggleContact={(id) => toggleSet(setSelectedLocalContactIds, id)}
                    onToggleUser={(id) => toggleSet(setSelectedLocalUserIds, id)}
                    onToggleCompany={(id) => toggleSet(setSelectedLocalCompanyIds, id)}
                    onExport={handleExport}
                    exporting={exporting}
                    contactsSearch={exportContactsSearch}
                    onContactsSearchChange={setExportContactsSearch}
                    contactsPage={exportContactsPage}
                    contactsLastPage={exportContactsLastPage}
                    contactsTotal={exportContactsTotal}
                    onContactsPageChange={setExportContactsPage}
                    usersSearch={exportUsersSearch}
                    onUsersSearchChange={setExportUsersSearch}
                    usersPage={exportUsersPage}
                    usersLastPage={exportUsersLastPage}
                    usersTotal={exportUsersTotal}
                    onUsersPageChange={setExportUsersPage}
                    companiesSearch={exportCompaniesSearch}
                    onCompaniesSearchChange={setExportCompaniesSearch}
                    companiesPage={exportCompaniesPage}
                    companiesLastPage={exportCompaniesLastPage}
                    companiesTotal={exportCompaniesTotal}
                    onCompaniesPageChange={setExportCompaniesPage}
                />
            )}
        </div>
    );
};

// =================================================================
// HELPER: toggle a value in a Set state
// =================================================================

function toggleSet<T>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, value: T) {
    setter((prev) => {
        const next = new Set(prev);
        if (next.has(value)) {
            next.delete(value);
        } else {
            next.add(value);
        }
        return next;
    });
}

// =================================================================
// SUB-COMPONENT: ImportView
// =================================================================

interface ImportViewProps {
    entityTab: EntityTab;
    searchQuery: string;
    onSearchChange: (q: string) => void;
    onBrowse: () => void;
    onLoadMore: () => void;
    browsing: boolean;
    contacts: HubSpotContact[];
    companies: HubSpotCompany[];
    nextCursor: string | null;
    selectedIds: Set<string>;
    onToggle: (id: string) => void;
    onToggleAll: () => void;
    importAs: 'contact' | 'user';
    onImportAsChange: (v: 'contact' | 'user') => void;
    companyOptions: { company_id: number; name: string }[];
    selectedCompanyId: string;
    onCompanyChange: (id: string) => void;
    onImport: () => void;
    importing: boolean;
}

const ImportView: React.FC<ImportViewProps> = ({
    entityTab,
    searchQuery,
    onSearchChange,
    onBrowse,
    onLoadMore,
    browsing,
    contacts,
    companies,
    nextCursor,
    selectedIds,
    onToggle,
    onToggleAll,
    importAs,
    onImportAsChange,
    companyOptions,
    selectedCompanyId,
    onCompanyChange,
    onImport,
    importing,
}) => {
    // Cast to a common shape for the select-all checkbox logic — both types
    // share hubspot_id and already_imported fields used by the table header
    const records: Array<{ hubspot_id: string; already_imported: boolean }> =
        entityTab === 'contacts' ? contacts : companies;
    const hasRecords = records.length > 0;

    return (
        <div>
            {/* Search bar + Browse button */}
            <div className="flex flex-col tablet:flex-row gap-12px mb-16px">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={
                        entityTab === 'contacts'
                            ? 'Search contacts by name, email, phone...'
                            : 'Search companies by name, domain...'
                    }
                    className="flex-1 px-12px py-8px border border-gray-300 rounded-lg text-16px desktop:text-14px focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent min-h-44px"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') onBrowse();
                    }}
                />
                <button
                    onClick={onBrowse}
                    disabled={browsing}
                    className="w-full tablet:w-auto px-20px py-8px text-14px font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 whitespace-nowrap min-h-44px"
                >
                    {browsing ? 'Loading...' : searchQuery ? 'Search' : 'Browse All'}
                </button>
            </div>

            {/* Import options for contacts — choose import type and target company */}
            {entityTab === 'contacts' && hasRecords && (
                <div className="flex flex-col tablet:flex-row tablet:items-center gap-12px mb-16px">
                    <label className="text-14px text-gray-600">Import as:</label>
                    <select
                        value={importAs}
                        onChange={(e) => onImportAsChange(e.target.value as 'contact' | 'user')}
                        className="w-full tablet:w-auto px-12px py-8px border border-gray-300 rounded-lg text-16px desktop:text-14px min-h-44px"
                    >
                        <option value="contact">Contact (customer contact, no login)</option>
                        <option value="user">User (system user with login)</option>
                    </select>

                    <label className="text-14px text-gray-600 ml-12px">Company:</label>
                    <select
                        value={selectedCompanyId}
                        onChange={(e) => onCompanyChange(e.target.value)}
                        className="w-full tablet:w-auto px-12px py-8px border border-gray-300 rounded-lg text-16px desktop:text-14px min-h-44px"
                    >
                        <option value="">Select company...</option>
                        {companyOptions.map((c) => (
                            <option key={c.company_id} value={String(c.company_id)}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {/* Results table */}
            {hasRecords && (
                <>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="w-full text-14px">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="w-40px px-12px py-8px">
                                        <Checkbox
                                            onChange={onToggleAll}
                                            checked={
                                                records.filter((r) => !r.already_imported).length > 0 &&
                                                records
                                                    .filter((r) => !r.already_imported)
                                                    .every((r) => selectedIds.has(r.hubspot_id))
                                            }
                                            hitArea="inset"
                                            ariaLabel="Select every importable record"
                                            className="hubspot-sync__select-all"
                                            dataRef="organism-hubspot-sync-select-all"
                                        />
                                    </th>
                                    {entityTab === 'contacts' ? (
                                        <>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Name</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Email</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Phone</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Company</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Status</th>
                                        </>
                                    ) : (
                                        <>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Name</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Phone</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Domain</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">City</th>
                                            <th className="px-12px py-8px text-left text-gray-600 font-medium">Status</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {entityTab === 'contacts'
                                    ? (contacts as HubSpotContact[]).map((c) => (
                                          <tr
                                              key={c.hubspot_id}
                                              className={`border-b border-gray-100 ${
                                                  c.already_imported ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                                              }`}
                                          >
                                              <td className="px-12px py-8px">
                                                  <Checkbox
                                                      checked={selectedIds.has(c.hubspot_id)}
                                                      onChange={() => onToggle(c.hubspot_id)}
                                                      disabled={c.already_imported}
                                                      hitArea="inset"
                                                      ariaLabel="Select this record"
                                                      className="hubspot-sync__select-row"
                                                      dataRef="organism-hubspot-sync-select-row"
                                                  />
                                              </td>
                                              <td className="px-12px py-8px font-medium text-gray-900">
                                                  {c.first_name} {c.last_name}
                                              </td>
                                              <td className="px-12px py-8px text-gray-600">{c.email || '—'}</td>
                                              <td className="px-12px py-8px text-gray-600">{c.phone || '—'}</td>
                                              <td className="px-12px py-8px text-gray-600">{c.company || '—'}</td>
                                              <td className="px-12px py-8px">
                                                  {c.already_imported ? (
                                                      <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-blue-100 text-blue-800">
                                                          Already Imported
                                                      </span>
                                                  ) : (
                                                      <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-green-100 text-green-800">
                                                          Available
                                                      </span>
                                                  )}
                                              </td>
                                          </tr>
                                      ))
                                    : (companies as HubSpotCompany[]).map((c) => (
                                          <tr
                                              key={c.hubspot_id}
                                              className={`border-b border-gray-100 ${
                                                  c.already_imported ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                                              }`}
                                          >
                                              <td className="px-12px py-8px">
                                                  <Checkbox
                                                      checked={selectedIds.has(c.hubspot_id)}
                                                      onChange={() => onToggle(c.hubspot_id)}
                                                      disabled={c.already_imported}
                                                      hitArea="inset"
                                                      ariaLabel="Select this record"
                                                      className="hubspot-sync__select-row"
                                                      dataRef="organism-hubspot-sync-select-row"
                                                  />
                                              </td>
                                              <td className="px-12px py-8px font-medium text-gray-900">{c.name}</td>
                                              <td className="px-12px py-8px text-gray-600">{c.phone || '—'}</td>
                                              <td className="px-12px py-8px text-gray-600">{c.domain || '—'}</td>
                                              <td className="px-12px py-8px text-gray-600">
                                                  {c.city ? `${c.city}, ${c.state || ''}` : '—'}
                                              </td>
                                              <td className="px-12px py-8px">
                                                  {c.already_imported ? (
                                                      <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-blue-100 text-blue-800">
                                                          Already Imported
                                                      </span>
                                                  ) : (
                                                      <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-green-100 text-green-800">
                                                          Available
                                                      </span>
                                                  )}
                                              </td>
                                          </tr>
                                      ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination + Import button */}
                    <div className="flex flex-col tablet:flex-row tablet:items-center tablet:justify-between gap-12px mt-16px">
                        <div className="flex items-center gap-12px">
                            {nextCursor && (
                                <button
                                    onClick={onLoadMore}
                                    disabled={browsing}
                                    className="px-16px py-8px text-14px font-medium text-brand-primary border border-brand-primary rounded-lg hover:bg-orange-50 disabled:opacity-50 min-h-44px"
                                >
                                    {browsing ? 'Loading...' : 'Load More'}
                                </button>
                            )}
                            <span className="text-14px text-gray-500">
                                {records.length} record{records.length !== 1 ? 's' : ''} loaded
                                {selectedIds.size > 0 && ` · ${selectedIds.size} selected`}
                            </span>
                        </div>

                        <button
                            onClick={onImport}
                            disabled={importing || selectedIds.size === 0}
                            className="w-full tablet:w-auto px-20px py-8px text-14px font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 min-h-44px"
                        >
                            {importing
                                ? 'Importing...'
                                : `Import ${selectedIds.size} Selected`}
                        </button>
                    </div>
                </>
            )}

            {/* Empty state — prompt to browse */}
            {!hasRecords && !browsing && (
                <div className="text-center py-32px text-gray-500">
                    <p className="text-14px">Click "Browse All" or enter a search query to find HubSpot {entityTab}.</p>
                </div>
            )}

            {/* Loading state */}
            {browsing && !hasRecords && (
                <div className="text-center py-32px">
                    <div className="inline-block w-24px h-24px border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin"></div>
                    <p className="mt-8px text-14px text-gray-500">Loading HubSpot {entityTab}...</p>
                </div>
            )}
        </div>
    );
};

// =================================================================
// SUB-COMPONENT: ExportView
// =================================================================

interface ExportViewProps {
    entityTab: EntityTab;
    loading: boolean;
    contacts: LocalContact[];
    users: LocalUser[];
    companies: LocalCompany[];
    selectedContactIds: Set<number>;
    selectedUserIds: Set<number>;
    selectedCompanyIds: Set<number>;
    onToggleContact: (id: number) => void;
    onToggleUser: (id: number) => void;
    onToggleCompany: (id: number) => void;
    onExport: () => void;
    exporting: boolean;
    /* Per-table search and pagination — each table is independent */
    contactsSearch: string;
    onContactsSearchChange: (value: string) => void;
    contactsPage: number;
    contactsLastPage: number;
    contactsTotal: number;
    onContactsPageChange: (page: number) => void;
    usersSearch: string;
    onUsersSearchChange: (value: string) => void;
    usersPage: number;
    usersLastPage: number;
    usersTotal: number;
    onUsersPageChange: (page: number) => void;
    companiesSearch: string;
    onCompaniesSearchChange: (value: string) => void;
    companiesPage: number;
    companiesLastPage: number;
    companiesTotal: number;
    onCompaniesPageChange: (page: number) => void;
}

const ExportView: React.FC<ExportViewProps> = ({
    entityTab,
    loading,
    contacts,
    users,
    companies,
    selectedContactIds,
    selectedUserIds,
    selectedCompanyIds,
    onToggleContact,
    onToggleUser,
    onToggleCompany,
    onExport,
    exporting,
    contactsSearch,
    onContactsSearchChange,
    contactsPage,
    contactsLastPage,
    contactsTotal,
    onContactsPageChange,
    usersSearch,
    onUsersSearchChange,
    usersPage,
    usersLastPage,
    usersTotal,
    onUsersPageChange,
    companiesSearch,
    onCompaniesSearchChange,
    companiesPage,
    companiesLastPage,
    companiesTotal,
    onCompaniesPageChange,
}) => {
    // Count total selected items for the export button label
    const totalSelected =
        entityTab === 'contacts'
            ? selectedContactIds.size + selectedUserIds.size
            : selectedCompanyIds.size;

    /* Compact "Showing X-Y of N" label rendered above each table */
    const rangeLabel = (page: number, items: number, total: number, perPage = 25): string => {
        if (total === 0) return 'No results';
        const start = (page - 1) * perPage + 1;
        const end = Math.min(start + items - 1, total);
        return `Showing ${start}-${end} of ${total}`;
    };

    return (
        <div>
            {/* Subtle loading bar — non-blocking, lets the search box stay interactive */}
            {loading && (
                <div className="flex items-center gap-8px text-14px text-gray-500 mb-8px">
                    <span className="inline-block w-12px h-12px border-2 border-gray-300 border-t-brand-primary rounded-full animate-spin" />
                    Loading...
                </div>
            )}
            {entityTab === 'contacts' ? (
                <>
                    {/* Contacts section */}
                    <div className="hubspot-sync__section-header flex items-center justify-between mb-8px gap-16px flex-wrap">
                        <h4 className="text-14px font-semibold text-gray-700">
                            Contacts <span className="font-normal text-gray-500">({rangeLabel(contactsPage, contacts.length, contactsTotal)})</span>
                        </h4>
                        <SearchBar
                            value={contactsSearch}
                            onChange={onContactsSearchChange}
                            placeholder="Search contacts..."
                            className="max-w-xs"
                        />
                    </div>
                    {contacts.length > 0 ? (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg mb-16px">
                            <table className="w-full text-14px">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="w-40px px-12px py-8px">
                                            <Checkbox checked={false} onChange={() => undefined} disabled hitArea="inset" ariaLabel="Selection unavailable" className="hubspot-sync__select-all" dataRef="organism-hubspot-sync-select-all" />
                                        </th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Name</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Email</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Phone</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Sync Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contacts.map((c) => (
                                        <tr key={`contact-${c.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-12px py-8px">
                                                <Checkbox
                                                    checked={selectedContactIds.has(c.id)}
                                                    onChange={() => onToggleContact(c.id)}
                                                    hitArea="inset"
                                                    ariaLabel="Select this record"
                                                    className="hubspot-sync__select-row"
                                                    dataRef="organism-hubspot-sync-select-row"
                                                />
                                            </td>
                                            <td className="px-12px py-8px font-medium text-gray-900">{c.full_name}</td>
                                            <td className="px-12px py-8px text-gray-600">{c.email || '—'}</td>
                                            <td className="px-12px py-8px text-gray-600">{c.phone || '—'}</td>
                                            <td className="px-12px py-8px">
                                                {c.hubspot_contact_id ? (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-blue-100 text-blue-800">
                                                        Synced
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-gray-100 text-gray-600">
                                                        Not Synced
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-14px text-gray-500 mb-16px">{contactsSearch ? 'No matching contacts.' : 'No contacts found.'}</p>
                    )}
                    <Pagination currentPage={contactsPage} totalPages={contactsLastPage} onPageChange={onContactsPageChange} />

                    {/* Users section */}
                    <div className="hubspot-sync__section-header flex items-center justify-between mt-24px mb-8px gap-16px flex-wrap">
                        <h4 className="text-14px font-semibold text-gray-700">
                            Users <span className="font-normal text-gray-500">({rangeLabel(usersPage, users.length, usersTotal)})</span>
                        </h4>
                        <SearchBar
                            value={usersSearch}
                            onChange={onUsersSearchChange}
                            placeholder="Search users..."
                            className="max-w-xs"
                        />
                    </div>
                    {users.length > 0 ? (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg mb-16px">
                            <table className="w-full text-14px">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="w-40px px-12px py-8px">
                                            <Checkbox checked={false} onChange={() => undefined} disabled hitArea="inset" ariaLabel="Selection unavailable" className="hubspot-sync__select-all" dataRef="organism-hubspot-sync-select-all" />
                                        </th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Name</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Email</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Role</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Sync Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={`user-${u.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-12px py-8px">
                                                <Checkbox
                                                    checked={selectedUserIds.has(u.id)}
                                                    onChange={() => onToggleUser(u.id)}
                                                    hitArea="inset"
                                                    ariaLabel="Select this record"
                                                    className="hubspot-sync__select-row"
                                                    dataRef="organism-hubspot-sync-select-row"
                                                />
                                            </td>
                                            <td className="px-12px py-8px font-medium text-gray-900">
                                                {u.first_name} {u.last_name}
                                            </td>
                                            <td className="px-12px py-8px text-gray-600">{u.email}</td>
                                            <td className="px-12px py-8px text-gray-600">
                                                {(u.role_names || []).join(', ') || '—'}
                                            </td>
                                            <td className="px-12px py-8px">
                                                {u.hubspot_contact_id ? (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-blue-100 text-blue-800">
                                                        Synced
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-gray-100 text-gray-600">
                                                        Not Synced
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-14px text-gray-500 mb-16px">{usersSearch ? 'No matching users.' : 'No users found.'}</p>
                    )}
                    <Pagination currentPage={usersPage} totalPages={usersLastPage} onPageChange={onUsersPageChange} />
                </>
            ) : (
                /* Companies export table */
                <>
                    <div className="hubspot-sync__section-header flex items-center justify-between mb-8px gap-16px flex-wrap">
                        <h4 className="text-14px font-semibold text-gray-700">
                            Companies <span className="font-normal text-gray-500">({rangeLabel(companiesPage, companies.length, companiesTotal)})</span>
                        </h4>
                        <SearchBar
                            value={companiesSearch}
                            onChange={onCompaniesSearchChange}
                            placeholder="Search companies..."
                            className="max-w-xs"
                        />
                    </div>
                    {companies.length > 0 ? (
                        <div className="overflow-x-auto border border-gray-200 rounded-lg mb-16px">
                            <table className="w-full text-14px">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="w-40px px-12px py-8px">
                                            <Checkbox checked={false} onChange={() => undefined} disabled hitArea="inset" ariaLabel="Selection unavailable" className="hubspot-sync__select-all" dataRef="organism-hubspot-sync-select-all" />
                                        </th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Name</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Phone</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Email</th>
                                        <th className="px-12px py-8px text-left text-gray-600 font-medium">Sync Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {companies.map((c) => (
                                        <tr key={c.company_id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="px-12px py-8px">
                                                <Checkbox
                                                    checked={selectedCompanyIds.has(c.company_id)}
                                                    onChange={() => onToggleCompany(c.company_id)}
                                                    hitArea="inset"
                                                    ariaLabel="Select this record"
                                                    className="hubspot-sync__select-row"
                                                    dataRef="organism-hubspot-sync-select-row"
                                                />
                                            </td>
                                            <td className="px-12px py-8px font-medium text-gray-900">{c.name}</td>
                                            <td className="px-12px py-8px text-gray-600">{c.phone || '—'}</td>
                                            <td className="px-12px py-8px text-gray-600">{c.billing_email || '—'}</td>
                                            <td className="px-12px py-8px">
                                                {c.hubspot_company_id ? (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-blue-100 text-blue-800">
                                                        Synced
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center px-8px py-2px rounded-full text-12px font-medium bg-gray-100 text-gray-600">
                                                        Not Synced
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-14px text-gray-500 mb-16px">{companiesSearch ? 'No matching companies.' : 'No companies found.'}</p>
                    )}
                    <Pagination currentPage={companiesPage} totalPages={companiesLastPage} onPageChange={onCompaniesPageChange} />
                </>
            )}

            {/* Export button — selection persists across pages, so totalSelected
                may exceed the rows currently visible. Show that explicitly. */}
            <div className="hubspot-sync__section-header flex items-center justify-between mt-24px gap-16px flex-wrap">
                <p className="text-14px text-gray-500">
                    {totalSelected > 0
                        ? `${totalSelected} record${totalSelected === 1 ? '' : 's'} selected across all pages`
                        : 'Select one or more records to export.'}
                </p>
                <button
                    onClick={onExport}
                    disabled={exporting || totalSelected === 0}
                    className="px-20px py-8px text-14px font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 disabled:opacity-50 min-h-44px"
                >
                    {exporting
                        ? 'Exporting...'
                        : `Export ${totalSelected} Selected to HubSpot`}
                </button>
            </div>
        </div>
    );
};

// =================================================================
// SUB-COMPONENT: ResultBanner
// =================================================================

interface ResultBannerProps {
    type: 'import' | 'export';
    result: ImportResult | ExportResult;
}

const ResultBanner: React.FC<ResultBannerProps> = ({ type, result }) => {
    // Determine counts based on operation type
    const isImport = type === 'import';
    const importResult = isImport ? (result as ImportResult) : null;
    const exportResult = !isImport ? (result as ExportResult) : null;

    const created = isImport ? importResult!.imported : exportResult!.exported;
    const updated = result.updated;
    const skipped = isImport ? importResult!.skipped : 0;
    const errors = result.errors;

    return (
        <div className="mb-16px p-16px rounded-lg bg-green-50 border border-green-200">
            <h4 className="text-14px font-semibold text-green-800 mb-8px">
                {isImport ? 'Import' : 'Export'} Complete
            </h4>
            <div className="flex gap-24px text-14px">
                <div>
                    <span className="text-green-700 font-medium">{created}</span>
                    <span className="text-green-600 ml-4px">{isImport ? 'imported' : 'exported'}</span>
                </div>
                <div>
                    <span className="text-blue-700 font-medium">{updated}</span>
                    <span className="text-blue-600 ml-4px">updated</span>
                </div>
                {isImport && skipped > 0 && (
                    <div>
                        <span className="text-yellow-700 font-medium">{skipped}</span>
                        <span className="text-yellow-600 ml-4px">skipped</span>
                    </div>
                )}
            </div>

            {/* Error details */}
            {errors.length > 0 && (
                <div className="mt-12px">
                    <p className="text-14px font-medium text-red-700 mb-4px">{errors.length} error(s):</p>
                    <ul className="list-disc list-inside text-14px text-red-600">
                        {errors.map((e, i) => (
                            <li key={i}>
                                {('name' in e && e.name) || ('hubspot_id' in e && `ID: ${e.hubspot_id}`)}: {e.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
